import {
  DispatchEntity,
  dispatchSelector,
  dispatchStatus,
  useDeleteDispatchMutation,
  useGetAllMessagesByObjectQuery,
  useGetDispatchCarryoversQuery,
  useGetDispatchCarryoverPolicyQuery,
  useGetDispatchNotificationsQuery,
  useGetDispatchQuery,
  useGetDispatchTrackingQuery,
  useGetObjectChangeLogsByObjectQuery,
  useGetSplitDispatchesByDispatchQuery,
  useUpdateDispatchMutation,
} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {useContext, useEffect, useMemo, useState} from "react";
import {AuthContext} from "../../app/AuthProvider";
import {CommentDialog} from "../../dialogs/CommentDialog";
import {ConfirmationDialog} from "../../dialogs/ConfirmationDialog";
import {EntityCommentDialog} from "../../dialogs/EntityCommentDialog";
import {DispatchNotifyDialog} from "../../dispatch/DispatchNotifyDialog";
import {OtsBrand, OtsColorSchemePreference, OtsThemeRoot} from "../../theme/OtsTheme";
import {
  getDispatchTrackingPollingInterval,
  isDispatchTrackingClosedStatus,
} from "../DispatchRequestProfile/dispatch-request-profile-utils";
import {DispatchProfile, DispatchProfileTab} from "./DispatchProfile";
import {getDispatchTitle} from "./dispatch-profile-utils";

export type DispatchProfilePageProps = {
  id: string;
  density?: "compact" | "default";
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
  initialTab?: DispatchProfileTab;
  /** Navigation is the host's: each action shows only when its callback is given. */
  onEdit?: (dispatch: DispatchEntity) => void;
  onOpenTicket?: (pickupTransportId: string, deliveryTransportId: string) => void;
  onOpenRequest?: (dispatchRequestId: string) => void;
  onOpenDispatch?: (dispatchId: string) => void;
  onDeleted?: () => void;
  onClose?: () => void;
};

const selectDispatch = dispatchSelector();

/** Data-connected dispatch profile for DispatchGo routes and the WaterGo React mount bridge. */
export const DispatchProfilePage = ({
  id,
  density = "compact",
  brand = "dispatch-go",
  colorScheme,
  initialTab,
  onEdit,
  onOpenTicket,
  onOpenRequest,
  onOpenDispatch,
  onDeleted,
  onClose,
}: DispatchProfilePageProps) => {
  const auth = useContext(AuthContext);
  const canEdit = auth?.hasAccess("dispatch_edit") ?? false;
  const canDelete = auth?.hasAccess("dispatch_delete") ?? false;

  const [commentOpen, setCommentOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelError, setCancelError] = useState<string>();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string>();

  const dispatchQuery = useGetDispatchQuery(id, {
    selectFromResult: (result) => ({...result, dispatch: selectDispatch(result)}),
  });
  const dispatch = dispatchQuery.dispatch;

  const carryoversQuery = useGetDispatchCarryoversQuery(dispatch?.carryoverCount ? id : skipToken);
  const messagesQuery = useGetAllMessagesByObjectQuery({objectId: id, objectType: "DISPATCH"});
  const notificationsQuery = useGetDispatchNotificationsQuery(id);
  const policyQuery = useGetDispatchCarryoverPolicyQuery(dispatch?.regionId ? {
    regionId: dispatch.regionId,
    personId: dispatch.personId,
    category: dispatch.category?.code,
  } : skipToken);
  const changeLogQuery = useGetObjectChangeLogsByObjectQuery({id, type: "DISPATCH"});
  const splitsQuery = useGetSplitDispatchesByDispatchQuery(dispatch?.splitGroupId ? id : skipToken);
  const splits = useMemo(() => splitsQuery.data?.map(split => DispatchEntity.from(split)), [splitsQuery.data]);

  const trackingClosed = !dispatch || isDispatchTrackingClosedStatus(dispatch.status?.code);
  const [pollingInterval, setPollingInterval] = useState(0);
  const trackingQuery = useGetDispatchTrackingQuery(trackingClosed ? skipToken : id, {
    pollingInterval,
    skipPollingIfUnfocused: true,
  });
  useEffect(() => {
    setPollingInterval(dispatch ? getDispatchTrackingPollingInterval(dispatch.status?.code, trackingQuery.data) : 0);
  }, [dispatch, trackingQuery.data]);

  const [updateDispatch, updateState] = useUpdateDispatchMutation();
  const [carryoverError, setCarryoverError] = useState<string>();
  const [deleteDispatch, deleteState] = useDeleteDispatchMutation();

  if (dispatchQuery.isLoading) {
    return <OtsThemeRoot brand={brand} colorScheme={colorScheme} className="p-6 text-sm text-[var(--ots-text-muted)]" role="status">Loading dispatch…</OtsThemeRoot>;
  }
  if (!dispatch) {
    return <OtsThemeRoot brand={brand} colorScheme={colorScheme} className="p-6 text-sm text-[var(--ots-error)]" role="alert">The dispatch could not be found.</OtsThemeRoot>;
  }

  const cancel = async (comment: string | null) => {
    if (!comment?.trim()) return;
    setCancelError(undefined);
    try {
      await updateDispatch({
        ...dispatch.asTypeValue(),
        status: dispatchStatus.canceled.code,
        lastModifiedDttm: Date.now(),
        comments: comment,
      }).unwrap();
      setCancelOpen(false);
    } catch {
      setCancelError("The dispatch could not be canceled. Please try again.");
    }
  };

  const setCarryoverExcluded = async (excluded: boolean) => {
    setCarryoverError(undefined);
    try {
      await updateDispatch({
        ...dispatch.asTypeValue(),
        carryoverExcluded: excluded,
        lastModifiedDttm: Date.now(),
      }).unwrap();
    } catch {
      setCarryoverError("The carryover setting could not be saved. Please try again.");
    }
  };

  const remove = async () => {
    setDeleteError(undefined);
    try {
      await deleteDispatch({id: dispatch.id, deviceId: "0", authentication: auth?.userId}).unwrap();
      setDeleteOpen(false);
      onDeleted?.();
    } catch {
      setDeleteError("The dispatch could not be deleted. Please try again.");
    }
  };

  const title = getDispatchTitle(dispatch);
  const requestId = dispatch.dispatchRequest?.id;

  return (
    <>
      <DispatchProfile
        dispatch={dispatch}
        carryovers={carryoversQuery.data}
        carryoversLoading={carryoversQuery.isLoading}
        messages={messagesQuery.data}
        messagesLoading={messagesQuery.isLoading}
        messagesError={messagesQuery.isError || notificationsQuery.isError}
        notifications={notificationsQuery.data}
        splits={splits}
        tracking={trackingQuery.data}
        trackingLoading={trackingQuery.isLoading}
        trackingFetching={trackingQuery.isFetching}
        trackingError={trackingQuery.error}
        trackingRefreshInterval={pollingInterval}
        changeLogs={changeLogQuery.data}
        changeLogsLoading={changeLogQuery.isLoading}
        density={density}
        brand={brand}
        colorScheme={colorScheme}
        initialTab={initialTab}
        onAddComment={() => setCommentOpen(true)}
        onNotify={canEdit ? () => setNotifyOpen(true) : undefined}
        onEdit={onEdit && canEdit ? () => onEdit(dispatch) : undefined}
        onCancelDispatch={canEdit ? () => {
          setCancelError(undefined);
          setCancelOpen(true);
        } : undefined}
        onDelete={onDeleted && canDelete ? () => {
          setDeleteError(undefined);
          setDeleteOpen(true);
        } : undefined}
        onOpenTicket={onOpenTicket && dispatch.pickupTransportId && dispatch.deliveryTransportId
          ? () => onOpenTicket(dispatch.pickupTransportId as string, dispatch.deliveryTransportId as string)
          : undefined}
        onOpenRequest={onOpenRequest && requestId ? () => onOpenRequest(requestId) : undefined}
        onOpenDispatch={onOpenDispatch}
        onRefreshTracking={trackingClosed ? undefined : () => trackingQuery.refetch()}
        onCarryoverExcludedChange={canEdit ? setCarryoverExcluded : undefined}
        carryoverUpdating={updateState.isLoading}
        carryoverError={carryoverError}
        carryoverPolicy={policyQuery.data}
        onClose={onClose}/>

      <DispatchNotifyDialog
        dispatchIds={[dispatch.id]}
        open={notifyOpen}
        onClose={() => setNotifyOpen(false)}
        brand={brand}
        colorScheme={colorScheme}/>

      {commentOpen && (
        <EntityCommentDialog
          target={{objectId: dispatch.id, objectType: "DISPATCH", label: title}}
          onClose={() => setCommentOpen(false)}/>
      )}

      <CommentDialog
        open={cancelOpen}
        title="Cancel this dispatch?"
        description="The driver will see it as canceled. Say why so the history explains it."
        label="Reason"
        submitLabel="Cancel dispatch"
        required
        requiredMessage="A reason is required to cancel a dispatch."
        messageType="Comment"
        error={cancelError}
        isSubmitting={updateState.isLoading}
        brand={brand}
        colorScheme={colorScheme}
        onSubmit={cancel}
        onCancel={() => setCancelOpen(false)}/>

      <ConfirmationDialog
        open={deleteOpen}
        tone="danger"
        title="Delete this dispatch?"
        description="This permanently removes the dispatch. It cannot be undone."
        confirmLabel="Delete dispatch"
        confirmingLabel="Deleting…"
        isConfirming={deleteState.isLoading}
        onConfirm={remove}
        onCancel={() => setDeleteOpen(false)}
        brand={brand}
        colorScheme={colorScheme}>
        {deleteError && <p role="alert" className="text-sm text-[var(--ots-error)]">{deleteError}</p>}
      </ConfirmationDialog>
    </>
  );
};
